// components/home/Departments.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaStethoscope,
  FaHeartbeat,
  FaBaby,
  FaUserMd,
  FaProcedures,
  FaXRay,
  FaMicroscope,
  FaCapsules,
  FaTooth,
  FaEye,
  FaFemale,
  FaBrain,
  FaLungs,
  FaBone,
  FaKidneys,
  FaSkull,
  FaHospital,
  FaPhoneAlt,
  FaCalendarCheck,
  FaUserInjured
} from 'react-icons/fa';
import { GiKidneys } from 'react-icons/gi';
import { TbStethoscope } from 'react-icons/tb';

const Departments = () => {
    const [selectedDept, setSelectedDept] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    // Comprehensive department data for Dr. Asrat Medium Clinic
    const departments = [
        {
            id: 1,
            name: "General Medicine",
            icon: <FaStethoscope className="text-3xl" />,
            description: "Comprehensive adult healthcare for common illnesses, chronic diseases, and preventive care.",
            services: ["Health Checkups", "Chronic Disease Management", "Fever & Infections", "Health Counseling"],
            doctors: 4,
            availability: "Mon-Sat: 8AM-8PM",
            color: "bg-blue-50 border-blue-200",
            textColor: "text-blue-700",
            category: "general"
        },
        {
            id: 2,
            name: "Pediatrics",
            icon: <FaBaby className="text-3xl" />,
            description: "Specialized care for infants, children, and adolescents with child-friendly environment.",
            services: ["Child Vaccinations", "Growth Monitoring", "Pediatric Emergencies", "Nutrition Counseling"],
            doctors: 3,
            availability: "Mon-Sat: 9AM-6PM",
            color: "bg-green-50 border-green-200",
            textColor: "text-green-700",
            category: "doctor"
        },
        {
            id: 3,
            name: "Surgery",
            icon: <FaProcedures className="text-3xl" />,
            description: "Minor surgical procedures with modern operation theater facilities.",
            services: ["Minor Procedures", "Wound Care", "Post-op Follow-up"],
            doctors: 2,
            availability: "Mon-Fri: 9AM-5PM (Emergency: 24/7)",
            color: "bg-red-50 border-red-200",
            textColor: "text-red-700",
            category: "surgical"
        },
        {
            id: 4,
            name: "Obstetrics & Gynecology",
            icon: <FaFemale className="text-3xl" />,
            description: "Comprehensive women's healthcare including pregnancy care and reproductive health.",
            services: ["Antenatal Care", "Family Planning", "Menopause Care"],
            doctors: 2,
            availability: "Mon-Sat: 8AM-6PM",
            color: "bg-pink-50 border-pink-200",
            textColor: "text-pink-700",
            category: "doctor"
        },
        {
            id: 5,
            name: "Orthopedics",
            icon: <FaBone className="text-3xl" />,
            description: "Diagnosis and treatment of musculoskeletal disorders, fractures, and joint problems.",
            services: ["Fracture Management", "Joint Pain Treatment", "Physiotherapy Referral", "Sports Injuries"],
            doctors: 1,
            availability: "Mon, Wed, Fri: 10AM-4PM",
            color: "bg-amber-50 border-amber-200",
            textColor: "text-amber-700",
            category: "doctor"
        },
        {
            id: 6,
            name: "Cardiology",
            icon: <FaHeartbeat className="text-3xl" />,
            description: "Heart care services including ECG, hypertension management, and cardiac consultation.",
            services: ["ECG Services", "Hypertension Management", "Cardiac Consultation", "Risk Assessment"],
            doctors: 1,
            availability: "Tue, Thu, Sat: 9AM-3PM",
            color: "bg-red-50 border-red-200",
            textColor: "text-red-700",
            category: "doctor"
        },
        {
            id: 7,
            name: "Dental Care",
            icon: <FaTooth className="text-3xl" />,
            description: "Complete dental services including extractions, fillings, and oral hygiene care.",
            services: ["Dental Checkup", "Tooth Extraction", "Filling & Cleaning", "Oral Health Education"],
            doctors: 1,
            availability: "Mon-Sat: 9AM-5PM",
            color: "bg-teal-50 border-teal-200",
            textColor: "text-teal-700",
            category: "doctor"
        },
        {
            id: 8,
            name: "Laboratory",
            icon: <FaMicroscope className="text-3xl" />,
            description: "Advanced diagnostic laboratory with rapid testing and accurate results.",
            services: ["Blood Tests", "Urine Analysis", "Microbiology"],
            doctors: 2,
            availability: "24/7 (Sample Collection: 7AM-7PM)",
            color: "bg-purple-50 border-purple-200",
            textColor: "text-purple-700",
            category: "diagnostic"
        },
        {
            id: 9,
            name: "Imaging & Radiology",
            icon: <FaXRay className="text-3xl" />,
            description: "Digital X-ray and ultrasound services for accurate diagnostic imaging.",
            services: ["Ultrasound Scan", "ECG", "Reporting Services"],
            doctors: 1,
            availability: "Mon-Sat: 8AM-7PM",
            color: "bg-cyan-50 border-cyan-200",
            textColor: "text-cyan-700",
            category: "diagnostic"
        },
        {
            id: 10,
            name: "Pharmacy",
            icon: <FaCapsules className="text-3xl" />,
            description: "24/7 pharmacy with all essential medicines and prescription services.",
            services: ["Medicine Dispensing", "Prescription Service", "Medical Supplies", "Health Products"],
            doctors: 2,
            availability: "24/7",
            color: "bg-indigo-50 border-indigo-200",
            textColor: "text-indigo-700",
            category: "support"
        },
        {
            id: 11,
            name: "Emergency",
            icon: <FaUserInjured className="text-3xl" />,
            description: "24/7 emergency medical services with trained staff and emergency equipment.",
            services: ["Trauma Care", "Emergency Resuscitation", "First Aid"],
            doctors: 3,
            availability: "24/7",
            color: "bg-rose-50 border-rose-200",
            textColor: "text-rose-700",
            category: "emergency"
        },
        {
            id: 12,
            name: "Chronic Disease Management",
            icon: <TbStethoscope className="text-3xl" />,
            description: "Specialized care for diabetes, hypertension, asthma and other chronic conditions.",
            services: ["Diabetes Care", "Hypertension Clinic", "Asthma Management", "Regular Follow-up"],
            doctors: 2,
            availability: "Mon-Sat: 9AM-5PM",
            color: "bg-emerald-50 border-emerald-200",
            textColor: "text-emerald-700",
            category: "doctor"
        },
        {
            id: 13,
            name: "ENT (Ear, Nose, Throat)",
            icon: <FaSkull className="text-3xl" />,
            description: "Treatment for ear, nose, and throat disorders with minor procedures.",
            services: ["Ear Infection", "Sinus Problems", "Throat Issues", "Hearing Tests"],
            doctors: 1,
            availability: "Tue, Thu, Sat: 10AM-2PM",
            color: "bg-violet-50 border-violet-200",
            textColor: "text-violet-700",
            category: "doctor"
        },
        {
            id: 14,
            name: "Dermatology",
            icon: <FaEye className="text-3xl" />,
            description: "Skin care and treatment for various dermatological conditions.",
            services: ["Skin Infections", "Allergy Treatment", "Skin Biopsy", "Cosmetic Procedures"],
            doctors: 1,
            availability: "Mon, Wed, Fri: 11AM-3PM",
            color: "bg-orange-50 border-orange-200",
            textColor: "text-orange-700",
            category: "doctor"
        },
        {
            id: 15,
            name: "Physiotherapy",
            icon: <FaLungs className="text-3xl" />,
            description: "Rehabilitation services for pain management and mobility improvement.",
            services: ["Pain Management", "Rehabilitation", "Exercise Therapy", "Post-surgery Recovery"],
            doctors: 1,
            availability: "Mon-Sat: 8AM-4PM",
            color: "bg-lime-50 border-lime-200",
            textColor: "text-lime-700",
            category: "support"
        }
    ];

    const categories = [
        { id: 'all', label: 'All Departments', count: departments.length },
        { id: 'general', label: 'General Medicine', count: departments.filter(d => d.category === 'general').length },
        { id: 'doctor', label: 'Doctors Care', count: departments.filter(d => d.category === 'doctor').length },
        { id: 'surgical', label: 'Surgical', count: departments.filter(d => d.category === 'surgical').length },
        { id: 'diagnostic', label: 'Diagnostic', count: departments.filter(d => d.category === 'diagnostic').length },
        { id: 'emergency', label: 'Emergency', count: departments.filter(d => d.category === 'emergency').length },
        { id: 'support', label: 'Support Services', count: departments.filter(d => d.category === 'support').length }
    ];

    const filteredDepartments = activeFilter === 'all' 
        ? departments 
        : departments.filter(dept => dept.category === activeFilter);

    const handleViewDetails = (dept) => {
        setSelectedDept(dept);
    };

    const closeModal = () => {
        setSelectedDept(null);
    };

    return (
        <section id="departments" className="py-20 bg-gradient-to-b from-white to-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl mb-6">
                        <FaHospital className="text-3xl text-blue-600" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        Our <span className="text-blue-600">Medical Departments</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                        Comprehensive healthcare services with specialized departments, 
                        modern facilities, and expert medical professionals.
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
                </motion.div>

                {/* Category Filters */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                    className="mb-10"
                >
                    <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setActiveFilter(category.id)}
                                className={`px-4 py-2.5 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                                    activeFilter === category.id
                                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                                {category.label}
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    activeFilter === category.id
                                        ? 'bg-white/20'
                                        : 'bg-gray-100'
                                }`}>
                                    {category.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Departments Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    {filteredDepartments.map((dept, index) => (
                        <motion.div
                            key={dept.id}
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            whileInView={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                            viewport={{ once: true, margin: "-50px" }}
                            whileHover={{ 
                                y: -8,
                                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)"
                            }}
                            className={`relative group rounded-2xl border-2 ${dept.color} p-6 transition-all duration-300 overflow-hidden cursor-pointer`}
                            onClick={() => handleViewDetails(dept)}
                        >
                            {/* Hover Effect Background */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            {/* Icon */}
                            <div className="relative z-10 flex justify-center mb-4">
                                <div className={`p-4 rounded-xl bg-white shadow-md group-hover:scale-110 transition-transform duration-300 ${dept.textColor}`}>
                                    {dept.icon}
                                </div>
                            </div>

                            {/* Department Name */}
                            <h3 className={`text-lg font-bold text-center mb-3 group-hover:text-blue-700 transition-colors ${dept.textColor}`}>
                                {dept.name}
                            </h3>

                            {/* Services Preview */}
                            <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                                {dept.services.slice(0, 2).map((service, idx) => (
                                    <span 
                                        key={idx}
                                        className="px-2 py-1 bg-white/80 text-xs font-medium text-gray-700 rounded-full"
                                    >
                                        {service}
                                    </span>
                                ))}
                                {dept.services.length > 2 && (
                                    <span className="px-2 py-1 bg-white/80 text-xs font-medium text-gray-500 rounded-full">
                                        +{dept.services.length - 2} more
                                    </span>
                                )}
                            </div>

                            {/* Availability Badge */}
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <FaCalendarCheck className="w-4 h-4 text-gray-500" />
                                <span className="text-xs font-medium text-gray-600">{dept.availability}</span>
                            </div>

                            {/* Doctors Count */}
                            <div className="flex items-center justify-center gap-2">
                                <FaUserMd className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-semibold text-gray-700">
                                    {dept.doctors} Doctor{dept.doctors > 1 ? 's' : ''}
                                </span>
                            </div>

                            {/* View Details Button */}
                            <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                <button className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-b-xl">
                                    View Details
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Department Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
                >
                    {[
                        { value: "7+", label: "Medical Departments", icon: "🏥" },
                        { value: "10+", label: "Doctors", icon: "👨‍⚕️" },
                        { value: "24/7", label: "Emergency Service", icon: "🚑" },
                        { value: "30+", label: "Medical Services", icon: "💊" }
                    ].map((stat, index) => (
                        <div key={index} className="p-6 bg-white rounded-2xl shadow-lg">
                            <div className="text-4xl mb-2">{stat.icon}</div>
                            <div className="text-3xl font-bold text-blue-600 mb-1">{stat.value}</div>
                            <div className="text-gray-600 font-medium">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>

                {/* Call to Action */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-blue-100 shadow-lg">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                            Need help choosing the right department?
                        </h3>
                        <p className="text-gray-700 mb-8 text-lg">
                            Our reception staff can guide you to the appropriate specialist for your condition.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-xl shadow-sm">
                                <FaPhoneAlt className="text-blue-600" />
                                <div>
                                    <div className="text-sm text-gray-600">Call for guidance</div>
                                    <div className="text-xl font-bold text-gray-900">+251-91313-4461</div>
                                </div>
                            </div>
                            <button className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300">
                                Book General Consultation
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Department Detail Modal */}
            <AnimatePresence>
                {selectedDept && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50"
                            onClick={closeModal}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="fixed inset-x-4 top-4 md:inset-x-auto md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bg-white rounded-2xl shadow-2xl z-50 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            {/* Modal Header */}
                            <div className={`p-6 ${selectedDept.color} rounded-t-2xl`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-xl">
                                            <div className={selectedDept.textColor}>
                                                {selectedDept.icon}
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900">{selectedDept.name}</h3>
                                            <p className="text-gray-600">{selectedDept.availability}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeModal}
                                        className="p-2 hover:bg-white/50 rounded-full transition-colors"
                                    >
                                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6">
                                <p className="text-gray-700 mb-6">{selectedDept.description}</p>

                                {/* Services */}
                                <div className="mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Services Offered</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {selectedDept.services.map((service, index) => (
                                            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                <span className="text-gray-700">{service}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Department Info */}
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="p-4 bg-blue-50 rounded-xl">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaUserMd className="text-blue-600" />
                                            <span className="font-medium text-gray-700">Doctors Available</span>
                                        </div>
                                        <div className="text-2xl font-bold text-blue-700">
                                            {selectedDept.doctors} Specialist{selectedDept.doctors > 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <div className="p-4 bg-green-50 rounded-xl">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FaCalendarCheck className="text-green-600" />
                                            <span className="font-medium text-gray-700">Timings</span>
                                        </div>
                                        <div className="font-semibold text-green-700">
                                            {selectedDept.availability}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                    <button className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300">
                                        Book Appointment
                                    </button>
                                    <button className="flex-1 py-3 bg-white text-blue-600 font-semibold rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-colors duration-300">
                                        View Doctors
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Departments;